/**
 * Data integration utilities for processing existing people.json and items.json
 */
import { transformPersonData } from './personDataProcessing';
import { transformItemData } from './itemDataProcessing';

/**
 * Process and integrate all people and items data
 * @param {Array} peopleData - Raw people data from people.json
 * @param {Object} itemsData - Raw items data from items.json
 * @returns {Object} Processed and integrated data
 */
export function processTeaCeremonyData(peopleData, itemsData) {
  try {
    // Transform people data
    const transformedPeople = peopleData.map(person =>
      transformPersonData(person, itemsData)
    );

    // Transform items data
    const transformedItems = Object.entries(itemsData).map(([itemName, connections]) =>
      transformItemData(itemName, connections, peopleData)
    );

    // Build cross-references
    const peopleWithReferences = buildPeopleReferences(transformedPeople, transformedItems);
    const itemsWithReferences = buildItemReferences(transformedItems, transformedPeople);

    return {
      people: peopleWithReferences,
      items: itemsWithReferences,
      stats: {
        totalPeople: peopleWithReferences.length,
        totalItems: itemsWithReferences.length,
        totalConnections: calculateTotalConnections(peopleWithReferences, itemsWithReferences)
      }
    };
  } catch (error) {
    console.error('Error processing tea ceremony data:', error);
    throw new Error('Failed to process tea ceremony data');
  }
}

/**
 * Build cross-references between people and items
 * @param {Array} people - Transformed people data
 * @param {Array} items - Transformed items data
 * @returns {Array} People with enhanced relationships
 */
function buildPeopleReferences(people, items) {
  return people.map(person => {
    // Find related people based on connections
    const relatedPeople = people
      .filter(otherPerson => otherPerson.id !== person.id)
      .filter(otherPerson => {
        return person.connections.some(connection =>
          connection.includes(otherPerson.name_jp) ||
          connection.includes(otherPerson.name_romaji)
        );
      })
      .slice(0, 8) // Limit to prevent overwhelming UI
      .map(relatedPerson => ({
        id: relatedPerson.id,
        name: {
          jp: relatedPerson.name_jp,
          en: relatedPerson.name_romaji
        },
        years: relatedPerson.years,
        relationship: extractRelationship(person.connections, relatedPerson)
      }));

    // Find family members (more specific relationship extraction)
    const familyMembers = people
      .filter(otherPerson => otherPerson.id !== person.id)
      .filter(otherPerson => {
        return person.connections.some(connection =>
          connection.includes('(祖父)') && connection.includes(otherPerson.name_jp) ||
          connection.includes('(父)') && connection.includes(otherPerson.name_jp) ||
          connection.includes('(母)') && connection.includes(otherPerson.name_jp) ||
          connection.includes('系') && connection.includes(otherPerson.name_jp)
        );
      })
      .map(familyMember => ({
        id: familyMember.id,
        name: {
          jp: familyMember.name_jp,
          en: familyMember.name_romaji
        },
        relationship: extractFamilyRelationship(person.connections, familyMember),
        years: familyMember.years
      }));

    return {
      ...person,
      relatedPeople,
      familyMembers
    };
  });
}

/**
 * Build cross-references for items
 * @param {Array} items - Transformed items data
 * @param {Array} people - Transformed people data
 * @returns {Array} Items with enhanced relationships
 */
function buildItemReferences(items, people) {
  return items.map(item => {
    // Find related items (items that share connections with people)
    const relatedItems = items
      .filter(otherItem => otherItem.id !== item.id)
      .filter(otherItem => {
        // Check if they share any people in common
        const itemPeopleIds = item.relatedPeople.map(p => p.id);
        const otherItemPeopleIds = otherItem.relatedPeople.map(p => p.id);
        return itemPeopleIds.some(id => otherItemPeopleIds.includes(id));
      })
      .slice(0, 6) // Limit for UI
      .map(relatedItem => ({
        id: relatedItem.id,
        name: relatedItem.name,
        type: relatedItem.type,
        relationship: 'related through provenance'
      }));

    return {
      ...item,
      relatedItems
    };
  });
}

/**
 * Extract relationship description between two people
 * @param {Array} connections - Person's connections array
 * @param {Object} otherPerson - Other person to find relationship with
 * @returns {string} Relationship description
 */
function extractRelationship(connections, otherPerson) {
  const relevantConnection = connections.find(connection =>
    connection.includes(otherPerson.name_jp) || connection.includes(otherPerson.name_romaji)
  );

  if (!relevantConnection) return 'connected';

  // Extract relationship context
  if (relevantConnection.includes('師')) return 'teacher/student';
  if (relevantConnection.includes('弟子')) return 'student';
  if (relevantConnection.includes('友')) return 'friend/colleague';
  if (relevantConnection.includes('系')) return 'school/lineage';
  if (relevantConnection.includes('兄')) return 'brother';
  if (relevantConnection.includes('父')) return 'father';
  if (relevantConnection.includes('祖父')) return 'grandfather';

  return 'connected';
}

/**
 * Extract specific family relationship
 * @param {Array} connections - Person's connections array
 * @param {Object} familyMember - Family member to find relationship with
 * @returns {string} Family relationship
 */
function extractFamilyRelationship(connections, familyMember) {
  const relevantConnection = connections.find(connection =>
    connection.includes(familyMember.name_jp) || connection.includes(familyMember.name_romaji)
  );

  if (!relevantConnection) return 'family';

  if (relevantConnection.includes('(祖父)')) return 'ancestor';
  if (relevantConnection.includes('(父)')) return 'father';
  if (relevantConnection.includes('(母)')) return 'mother';
  if (relevantConnection.includes('系')) return 'descendant';

  return 'family';
}

/**
 * Calculate total connections across all data
 * @param {Array} people - People data
 * @param {Array} items - Items data
 * @returns {number} Total connections count
 */
function calculateTotalConnections(people, items) {
  const peopleConnections = people.reduce((total, person) => {
    return total + (person.relatedPeople?.length || 0) + (person.relatedItems?.length || 0);
  }, 0);

  const itemConnections = items.reduce((total, item) => {
    return total + (item.relatedPeople?.length || 0) + (item.relatedItems?.length || 0);
  }, 0);

  // Avoid double counting
  return Math.floor((peopleConnections + itemConnections) / 2);
}

/**
 * Search through integrated data
 * @param {Object} data - Integrated data from processTeaCeremonyData
 * @param {string} query - Search query
 * @param {string} language - Language for search ('en' or 'jp')
 * @returns {Object} Search results
 */
export function searchIntegratedData(data, query, language = 'en') {
  if (!query || query.length < 2) {
    return { people: [], items: [] };
  }

  const lowerQuery = query.toLowerCase();

  const peopleResults = data.people.filter(person => {
    const nameMatch = language === 'jp'
      ? person.name_jp.includes(query)
      : person.name_romaji.toLowerCase().includes(lowerQuery);

    const descriptionMatch = language === 'jp'
      ? person.description.includes(query)
      : person.biography?.en?.toLowerCase().includes(lowerQuery);

    return nameMatch || descriptionMatch;
  });

  const itemResults = data.items.filter(item => {
    const nameMatch = language === 'jp'
      ? item.name.jp.includes(query)
      : item.name.en.toLowerCase().includes(lowerQuery);

    const descriptionMatch = language === 'jp'
      ? item.description?.jp?.includes(query)
      : item.description?.en?.toLowerCase().includes(lowerQuery);

    return nameMatch || descriptionMatch;
  });

  return {
    people: peopleResults.slice(0, 10), // Limit results
    items: itemResults.slice(0, 10)
  };
}

/**
 * Filter data by criteria
 * @param {Object} data - Integrated data
 * @param {Object} filters - Filter criteria
 * @returns {Object} Filtered data
 */
export function filterIntegratedData(data, filters = {}) {
  let filteredPeople = [...data.people];
  let filteredItems = [...data.items];

  // Filter by period/era
  if (filters.period) {
    filteredPeople = filteredPeople.filter(person => {
      if (filters.period === 'ancient' && person.deathYear && person.deathYear < 1000) return true;
      if (filters.period === 'medieval' && person.deathYear && person.deathYear >= 1000 && person.deathYear < 1600) return true;
      if (filters.period === 'edo' && person.deathYear && person.deathYear >= 1600 && person.deathYear < 1868) return true;
      if (filters.period === 'modern' && person.deathYear && person.deathYear >= 1868) return true;
      return false;
    });
  }

  // Filter items by type
  if (filters.itemType) {
    filteredItems = filteredItems.filter(item => item.type === filters.itemType);
  }

  return {
    people: filteredPeople,
    items: filteredItems,
    stats: {
      totalPeople: filteredPeople.length,
      totalItems: filteredItems.length
    }
  };
}